import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Layers, CheckCircle2, ScanBarcode } from 'lucide-react';
import { HelpTooltip } from '../../shared/HelpTooltip';

/**
 * ScanQueue - Component displaying the current batch of scanned items
 * 
 * Features:
 * - Progress bar showing items scanned vs minimum required
 * - Visual feedback when ready to post
 * - Individual item removal
 * - Animating "Review & Post" button when ready
 */
const ScanQueue = ({
  items = [],
  minItemsRequired = 2,
  onReviewClick,
  onClearQueue,
  onRemoveItem,
  t = (key, fallback) => fallback
}) => {
  if (items.length === 0) return null;
  
  const progress = Math.min(100, (items.length / minItemsRequired) * 100);
  const canPost = items.length >= minItemsRequired;
  
  return (
    <Card className={`mb-4 border-2 transition-all ${
      canPost 
        ? 'border-green-400 bg-green-50/50 shadow-md' 
        : 'border-blue-200 bg-blue-50/30'
    }`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-sm flex items-center gap-2 ${
            canPost ? 'text-green-700' : 'text-blue-700'
          }`}>
            <Layers className="w-4 h-4" />
            {t('scanner_ui.items_in_queue', 'Items in Queue')}
            <Badge 
              variant={canPost ? 'default' : 'secondary'} 
              className={canPost ? 'bg-green-500' : ''}
            >
              {items.length}/{minItemsRequired}
            </Badge>
            <HelpTooltip portal="retailer" category="scanner" tooltipKey="queue" size="sm" />
          </CardTitle>
          
          <div className="flex gap-2">
            {canPost && (
              <Button 
                size="sm" 
                onClick={onReviewClick}
                className="bg-green-600 hover:bg-green-700 animate-pulse"
                data-testid="review-queue-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {t('scanner_ui.review_and_post', 'Review & Post')}
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onClearQueue}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {t('scanner_ui.clear_queue', 'Clear')}
            </Button>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2">
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-500 ${
                canPost ? 'bg-green-500' : 'bg-blue-400'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-2">
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <div 
              key={index}
              className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2 shadow-sm hover:shadow transition-shadow"
            >
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-gray-700 truncate">
                  {item.raw_code}
                </p>
                {item.name && (
                  <p className="text-xs text-gray-500 truncate">
                    {item.name.substring(0, 25)}
                    {item.name.length > 25 ? '...' : ''}
                  </p>
                )}
              </div>
              <button
                onClick={() => onRemoveItem(index)}
                className="p-1 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors"
                data-testid={`remove-queue-item-${index}`}
                aria-label={`Remove ${item.name || item.raw_code}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        
        {!canPost && (
          <p className="text-sm text-blue-600 mt-3 flex items-center gap-2">
            <ScanBarcode className="w-4 h-4" />
            {t('scanner_ui.scan_more_items', 
              `Scan ${minItemsRequired - items.length} more item(s) to enable posting`
            )}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ScanQueue;
